import React, { useEffect, useState } from "react";
import "./UserView.css";
import "../tables/Button.css";
import "../SafetyObservation/safetyObservation.css";
import "../SafetyMeetings/safetyMeetings.css";
import "../HsePolicies/hsePolicies.css";
import { useNavigate, useParams } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import axios from "axios";
import { IoMdArrowBack } from "react-icons/io";
import { FiDownload, FiEye, FiTrash2 } from "react-icons/fi";
import API_URL from "../../src/Config";
import {
  downloadSafetyFile,
  formatDateDisplay,
  openSafetyFile,
  parseApiError,
} from "../SafetyObservation/observationUtils";
import {
  getPolicyDocumentName,
  getPolicyDocumentSizeLabel,
} from "../HsePolicies/hsePolicyUtils";

function HsePolicyView({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const { policyId } = useParams();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [isDeletingDocument, setIsDeletingDocument] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const fetchPolicy = async () => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");

      const response = await axios.get(
        `${API_URL}/api/safety/hse-policies/${policyId}`,
        {
          headers: {
            Authorization: authKey,
          },
        },
      );

      setPolicy(response.data);
    } catch (error) {
      showSnackbar(parseApiError(error, "Failed to fetch HSE policy"), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPolicy();
  }, [policyId]);

  const handleDownload = async (document) => {
    try {
      await downloadSafetyFile(document.id, document.filename);
    } catch (error) {
      showSnackbar(parseApiError(error, "Failed to download file"), "error");
    }
  };

  const handleViewFile = async (document) => {
    try {
      await openSafetyFile(document.id);
    } catch (error) {
      showSnackbar(parseApiError(error, "Failed to open file"), "error");
    }
  };

  const handleAcknowledge = async () => {
    try {
      setIsAcknowledging(true);
      const authKey = localStorage.getItem("authKey");

      const response = await axios.post(
        `${API_URL}/api/safety/hse-policies/${policyId}/acknowledge`,
        {},
        {
          headers: {
            Authorization: authKey,
            "Content-Type": "application/json",
          },
        },
      );

      showSnackbar(response.data?.message || "Policy acknowledged");
      await fetchPolicy();
    } catch (error) {
      showSnackbar(
        parseApiError(error, "Failed to acknowledge HSE policy"),
        "error",
      );
    } finally {
      setIsAcknowledging(false);
    }
  };

  const handleDeletePolicy = async () => {
    try {
      setIsDeleting(true);
      const authKey = localStorage.getItem("authKey");

      const response = await axios.delete(
        `${API_URL}/api/safety/hse-policies/${policyId}`,
        {
          headers: {
            Authorization: authKey,
          },
        },
      );

      showSnackbar(response.data?.message || "Policy deleted successfully");
      setShowDeleteConfirm(false);
      window.setTimeout(() => {
        navigate("/hse-policies");
      }, 700);
    } catch (error) {
      showSnackbar(parseApiError(error, "Failed to delete policy"), "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteDocument = async () => {
    if (!documentToDelete) return;

    try {
      setIsDeletingDocument(true);
      const authKey = localStorage.getItem("authKey");

      const response = await axios.delete(
        `${API_URL}/api/safety/hse-policies/documents/${documentToDelete.id}`,
        {
          headers: {
            Authorization: authKey,
          },
        },
      );

      showSnackbar(response.data?.message || "Document deleted successfully");
      setDocumentToDelete(null);
      await fetchPolicy();
    } catch (error) {
      showSnackbar(parseApiError(error, "Failed to delete document"), "error");
    } finally {
      setIsDeletingDocument(false);
    }
  };

  const acknowledgments = Array.isArray(policy?.acknowledgments)
    ? policy.acknowledgments
    : [];

  return (
    <>
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content observation-view-page">
          <div className="data-list-header">
            <div className="data-list-header-content">
              <div className="data-list-heading-content-left-side">
                <Tooltip title="Back" arrow>
                  <IoMdArrowBack
                    className="back-company"
                    onClick={() => navigate("/hse-policies")}
                  />
                </Tooltip>
                HSE Policy View
              </div>
              <div className="observation-view-actions">
                <span
                  className={`hse-policy-ack-status ${
                    policy?.acknowledged_by_me
                      ? "acknowledged"
                      : "not-acknowledged"
                  }`}
                >
                  {policy?.acknowledged_by_me
                    ? "Acknowledged"
                    : "Not Acknowledged"}
                </span>
                {policy && !policy?.acknowledged_by_me && (
                  <button
                    className="button-bg"
                    onClick={handleAcknowledge}
                    disabled={isAcknowledging}
                  >
                    {isAcknowledging ? "Acknowledging..." : "Acknowledge"}
                  </button>
                )}
                {policy?.acknowledged_by_me && (
                  <button className="button-bg" disabled>
                    Acknowledged
                  </button>
                )}
                {/* <button
                  className="button-bg"
                  onClick={() => navigate(`/edit-hse-policy/${policyId}`)}
                >
                  Edit
                </button>
                <button
                  className="button-bg"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete
                </button> */}
              </div>
            </div>
          </div>

          <div className="header-separator"></div>

          {loading ? (
            <div className="loader-container">
              <div className="loader"></div>
              <div>Loading...</div>
            </div>
          ) : policy ? (
            <>
              <div className="observation-section-card">
                <div className="observation-section-title">Policy Details</div>
                <div className="observation-section-body">
                  {!policy.acknowledged_by_me && (
                    <div className="hse-policy-callout">
                      Review the policy details and attached documents, then use
                      the acknowledge button to confirm you have read this
                      policy.
                    </div>
                  )}
                  <div className="observation-detail-grid">
                    <div className="observation-detail-item">
                      <span className="observation-detail-label">Title</span>
                      <span>{policy.title || "NA"}</span>
                    </div>
                    <div className="observation-detail-item">
                      <span className="observation-detail-label">Category</span>
                      <span>{policy.category || "NA"}</span>
                    </div>
                    <div className="observation-detail-item">
                      <span className="observation-detail-label">
                        Effective Date
                      </span>
                      <span>{formatDateDisplay(policy.effective_date)}</span>
                    </div>
                    <div className="observation-detail-item">
                      <span className="observation-detail-label">
                        Created By
                      </span>
                      <span>{policy.created_by_name || "NA"}</span>
                    </div>
                    <div className="observation-detail-item form-group-full-width">
                      <span className="observation-detail-label">
                        Description
                      </span>
                      <span className="hse-policy-detail-text">
                        {policy.description || "NA"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="observation-section-card">
                <div className="observation-section-title">Acknowledgments</div>
                <div className="observation-section-body">
                  {acknowledgments.length > 0 ? (
                    <div className="hse-policy-ack-list">
                      {acknowledgments.map((acknowledgment) => (
                        <span
                          key={`${acknowledgment.user_id}-${acknowledgment.acknowledged_at}`}
                          className="hse-policy-ack-chip"
                        >
                          {acknowledgment.user_name || "NA"} |{" "}
                          {formatDateDisplay(acknowledgment.acknowledged_at)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="observation-empty-state">
                      No acknowledgments available.
                    </div>
                  )}
                </div>
              </div>

              <div className="observation-section-card">
                <div className="observation-section-title">Documents</div>
                <div className="observation-section-body">
                  {Array.isArray(policy.documents) &&
                  policy.documents.length > 0 ? (
                    <div className="observation-attachments">
                      {policy.documents.map((document) => (
                        <div
                          key={document.id}
                          className="observation-attachment-row"
                        >
                          <div className="observation-attachment-meta">
                            <div className="observation-attachment-name">
                              {getPolicyDocumentName(document)}
                            </div>
                            <div className="observation-attachment-subtext">
                              {document.file_type || "NA"} |{" "}
                              {getPolicyDocumentSizeLabel(document)}
                            </div>
                          </div>
                          <div className="observation-attachment-actions">
                            <Tooltip title="View" arrow>
                              <button
                                className="observation-icon-button"
                                type="button"
                                onClick={() => handleViewFile(document)}
                              >
                                <FiEye />
                              </button>
                            </Tooltip>
                            <Tooltip title="Download" arrow>
                              <button
                                className="observation-icon-button"
                                type="button"
                                onClick={() => handleDownload(document)}
                              >
                                <FiDownload />
                              </button>
                            </Tooltip>
                            {/* <Tooltip title="Delete" arrow>
                              <button
                                className="observation-icon-button observation-icon-button-danger"
                                type="button"
                                onClick={() => setDocumentToDelete(document)}
                              >
                                <FiTrash2 />
                              </button>
                            </Tooltip> */}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="observation-empty-state">
                      No documents uploaded.
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="no-users-container">
              <div className="no-users-message">HSE Policy not found</div>
            </div>
          )}
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="confirm-delete-popup">
          <div className="confirm-delete-popup-content">
            <div className="confirm-model">
              <div className="confirm-model-content">
                <div className="confirm-model-content-body">
                  <p>
                    Are you sure you want to delete policy{" "}
                    <strong>{policy?.title || "this item"}</strong>?
                  </p>
                </div>
                <div className="confirm-model-content-footer">
                  <button
                    className="confirm-delete-button"
                    onClick={handleDeletePolicy}
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Deleting..." : "Yes"}
                  </button>
                  <button
                    className="confirm-cancel-button"
                    onClick={() => !isDeleting && setShowDeleteConfirm(false)}
                    disabled={isDeleting}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {documentToDelete && (
        <div className="confirm-delete-popup">
          <div className="confirm-delete-popup-content">
            <div className="confirm-model">
              <div className="confirm-model-content">
                <div className="confirm-model-content-body">
                  <p>
                    Are you sure you want to delete document{" "}
                    <strong>{documentToDelete.filename || "this file"}</strong>?
                  </p>
                </div>
                <div className="confirm-model-content-footer">
                  <button
                    className="confirm-delete-button"
                    onClick={handleDeleteDocument}
                    disabled={isDeletingDocument}
                  >
                    {isDeletingDocument ? "Deleting..." : "Yes"}
                  </button>
                  <button
                    className="confirm-cancel-button"
                    onClick={() =>
                      !isDeletingDocument && setDocumentToDelete(null)
                    }
                    disabled={isDeletingDocument}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default HsePolicyView;
